export const QR_EDITOR_BLOCK_TYPES = [
  {
    type: 'text',
    side: 'front',
    label: 'Text',
    description: 'Editable text such as the restaurant name, subtitle, or call to action.',
  },
  {
    type: 'logo',
    label: 'Logo',
    description: 'Restaurant logo with optional upload, movement, and resizing.',
  },
  {
    type: 'qr',
    label: 'QR code',
    description: 'Required QR element with configurable size, colors, and placement.',
  },
  {
    type: 'nfc',
    label: 'NFC tap area',
    description: 'Visible NFC tap indicator with icon, text, size, and placement. The chip itself stays inside the physical product.',
  },
  {
    type: 'image',
    label: 'Image',
    description: 'Optional decorative or branded image.',
  },
  {
    type: 'product_color',
    label: 'Product color',
    description: 'Controls the physical product or accent color.',
  },
  {
    type: 'background_color',
    label: 'Background color',
    description: 'Controls the printable design surface background.',
  },
  {
    type: 'material',
    label: 'Material',
    description: 'Lets the customer choose from this product’s material options.',
  },
  {
    type: 'surface_finish',
    label: 'Surface finish',
    description: 'Glossy, matte, frosted, or other product finishes.',
  },
  {
    type: 'three_d_property',
    label: '3D property',
    description: 'A numeric 3D setting such as thickness or stand angle.',
  },
];

const uid = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const makeQrEditorBlock = (type = 'text') => {
  if (type === 'qr') {
    return {
      id: uid('qr'),
      type,
      side: 'front',
      label: 'QR code',
      enabled: true,
      required: true,
      default: {
        x: 50,
        y: 50,
        size: 34,
        foreground_color: '#111111',
        background_color: '#ffffff',
      },
      settings: {
        movable: true,
        resizable: true,
        color_editable: true,
        min_size: 18,
        max_size: 60,
      },
    };
  }

  if (type === 'nfc') {
    return {
      id: uid('nfc'),
      type,
      side: 'front',
      label: 'NFC tap area',
      enabled: true,
      required: false,
      default: {
        x: 28,
        y: 50,
        size: 18,
        icon: 'waves',
        text: 'Tap to view our menu',
        show_text: true,
        color: '#111111',
      },
      settings: {
        movable: true,
        resizable: true,
        color_editable: true,
        text_editable: true,
        show_text_toggle: true,
        min_size: 8,
        max_size: 44,
      },
    };
  }

  if (type === 'logo') {
    return {
      id: uid('logo'),
      type,
      side: 'front',
      label: 'Restaurant logo',
      enabled: true,
      required: false,
      default: {
        x: 50,
        y: 14,
        width: 24,
        opacity: 1,
      },
      settings: {
        replaceable: true,
        movable: true,
        resizable: true,
        opacity_editable: true,
        min_width: 8,
        max_width: 60,
      },
    };
  }

  if (type === 'image') {
    return {
      id: uid('image'),
      type,
      side: 'front',
      label: 'Custom image',
      enabled: true,
      required: false,
      default: {
        x: 50,
        y: 50,
        width: 28,
        opacity: 1,
      },
      settings: {
        uploadable: true,
        movable: true,
        resizable: true,
        opacity_editable: true,
        max_files: 1,
      },
    };
  }

  if (type === 'product_color') {
    return {
      id: uid('product-color'),
      type,
      label: 'Product color',
      enabled: true,
      required: false,
      default: '',
      settings: {
        allow_custom_color: false,
        use_product_options: true,
        target: 'product',
      },
    };
  }

  if (type === 'background_color') {
    return {
      id: uid('background-color'),
      type,
      label: 'Background color',
      enabled: true,
      required: false,
      default: '#ffffff',
      settings: {
        allow_custom_color: true,
        target: 'design_surface',
      },
    };
  }

  if (type === 'material') {
    return {
      id: uid('material'),
      type,
      label: 'Material',
      enabled: true,
      required: false,
      default: '',
      settings: {
        use_product_options: true,
      },
    };
  }

  if (type === 'surface_finish') {
    return {
      id: uid('finish'),
      type,
      label: 'Surface finish',
      enabled: true,
      required: false,
      default: 'glossy',
      settings: {
        options: [
          { value: 'glossy', label: 'Glossy' },
          { value: 'matte', label: 'Matte' },
          { value: 'frosted', label: 'Frosted' },
        ],
      },
    };
  }

  if (type === 'three_d_property') {
    return {
      id: uid('3d-property'),
      type,
      label: 'Thickness',
      enabled: true,
      required: false,
      default: 10,
      settings: {
        property: 'thickness',
        min: 4,
        max: 22,
        step: 1,
        unit: 'mm',
      },
    };
  }

  return {
    id: uid('text'),
    type: 'text',
    side: 'front',
    label: 'Text',
    enabled: true,
    required: false,
    default: {
      text: 'New text',
      x: 50,
      y: 18,
      font_size: 4.2,
      color: '#1f2937',
      align: 'center',
      weight: 700,
    },
    settings: {
      text_editable: true,
      movable: true,
      resizable: true,
      color_editable: true,
      font_editable: true,
      align_editable: true,
      min_font_size: 1.8,
      max_font_size: 9,
      max_length: 90,
    },
  };
};

export const DEFAULT_QR_EDITOR_SCHEMA = {
  version: 2,
  sides: {
    front: { enabled: true, label: 'Front' },
    back: { enabled: false, label: 'Back' },
  },
  blocks: [
    {
      ...makeQrEditorBlock('text'),
      id: 'restaurant-name',
      label: 'Restaurant name',
      required: true,
      default: {
        text: '',
        x: 50,
        y: 13,
        font_size: 4.8,
        color: '#1f2937',
        align: 'center',
        weight: 700,
      },
    },
    {
      ...makeQrEditorBlock('qr'),
      id: 'main-qr',
    },
    {
      ...makeQrEditorBlock('text'),
      id: 'call-to-action',
      label: 'Call to action',
      default: {
        text: 'Scan to view our menu',
        x: 50,
        y: 84,
        font_size: 3.2,
        color: '#1f2937',
        align: 'center',
        weight: 700,
      },
    },
    {
      ...makeQrEditorBlock('product_color'),
      id: 'product-color',
    },
    {
      ...makeQrEditorBlock('material'),
      id: 'material',
    },
  ],
};

export const normalizeQrEditorSchema = (schema) => {
  if (!schema || !Array.isArray(schema.blocks)) {
    return DEFAULT_QR_EDITOR_SCHEMA;
  }

  return {
    version: Number(schema.version || 2),
    sides: {
      front: {
        enabled: schema?.sides?.front?.enabled !== false,
        label: schema?.sides?.front?.label || 'Front',
      },
      back: {
        enabled: schema?.sides?.back?.enabled === true,
        label: schema?.sides?.back?.label || 'Back',
      },
    },
    blocks: schema.blocks
      .filter((block) => block && block.id && block.type)
      .map((block) => ({
        ...block,
        side: block.side === 'back' ? 'back' : 'front',
      })),
  };
};
